import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { LogOut, User, Clock, Shield, Eye } from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ImpersonationBanner Component
 * 
 * Displays a prominent banner when impersonation is active:
 * - Super admin impersonating clinic user (amber/orange/red gradient)
 * - Clinic admin/staff viewing patient portal (teal/blue gradient)
 * 
 * Following industry best practices for impersonation UX:
 * - Highly visible warning banner
 * - Clear indication of who is being impersonated
 * - Easy exit mechanism
 * - Session time remaining
 */
export default function ImpersonationBanner({ 
  impersonationData, 
  onStopImpersonation,
  className = ""
}) {
  const [stopping, setStopping] = React.useState(false);
  const [timeRemaining, setTimeRemaining] = React.useState('');

  // Determine impersonation type
  const isPatientImpersonation = impersonationData?.type === 'patient_impersonation';
  
  // Calculate time remaining
  React.useEffect(() => {
    if (!impersonationData?.expires_at) return;

    const updateTime = () => {
      const now = new Date();
      const expires = new Date(impersonationData.expires_at);
      const diff = expires - now;

      if (diff <= 0) {
        setTimeRemaining('Expired');
        // Auto-stop impersonation when expired
        handleStopImpersonation();
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      
      if (hours > 0) {
        setTimeRemaining(`${hours}h ${minutes}m remaining`);
      } else {
        setTimeRemaining(`${minutes}m remaining`);
      }
    };

    updateTime();
    const interval = setInterval(updateTime, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [impersonationData?.expires_at]);

  const handleStopImpersonation = async () => {
    setStopping(true);
    try {
      // Use different endpoints based on impersonation type
      const endpoint = isPatientImpersonation 
        ? `${API_URL}/api/clinic/stop-patient-impersonation`
        : `${API_URL}/api/admin/stop-impersonation`;
      
      const response = await axios.post(endpoint);
      
      if (response.data.success) {
        // Clear impersonation data from localStorage
        localStorage.removeItem('impersonation_data');
        localStorage.removeItem('patient_impersonation_data');
        
        // Update token and user
        localStorage.setItem('token', response.data.access_token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        
        const successMessage = isPatientImpersonation
          ? 'Patient portal view ended. You are now back as yourself.'
          : 'Impersonation session ended. You have returned to your admin account.';
        
        toast.success(successMessage);
        
        // Callback to parent
        if (onStopImpersonation) {
          onStopImpersonation(response.data);
        }
        
        // Redirect to appropriate dashboard
        const returnUrl = response.data.return_url || (isPatientImpersonation ? '/admin' : '/super-admin');
        window.location.href = returnUrl;
      }
    } catch (error) {
      console.error('Error stopping impersonation:', error);
      toast.error(error.response?.data?.detail || 'Error ending session');
    } finally {
      setStopping(false);
    }
  };

  if (!impersonationData) return null;

  // Get display info based on impersonation type
  const displayUser = isPatientImpersonation 
    ? impersonationData.impersonated_patient 
    : impersonationData.impersonated_user;
  
  // Choose banner style based on type
  const bannerGradient = isPatientImpersonation
    ? 'from-teal-500 via-cyan-500 to-blue-500'  // Teal/blue for patient view
    : 'from-amber-500 via-orange-500 to-red-500';  // Original amber/red for super admin
  
  const bannerLabel = isPatientImpersonation 
    ? 'PATIENT VIEW MODE' 
    : 'IMPERSONATION MODE';
  
  const bannerIcon = isPatientImpersonation ? Eye : Shield;
  const BannerIcon = bannerIcon;

  return (
    <div 
      className={`fixed top-0 left-0 right-0 z-[9999] bg-gradient-to-r ${bannerGradient} text-white shadow-lg ${className}`}
      data-testid="impersonation-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          {/* Left: Warning and user info */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-white/20 rounded-full px-3 py-1">
              <BannerIcon className="h-4 w-4" />
              <span className="text-sm font-semibold">{bannerLabel}</span>
            </div>
            
            <div className="flex items-center gap-2">
              <User className="h-4 w-4" />
              <span className="text-sm">
                {isPatientImpersonation ? 'Viewing as: ' : 'Impersonating: '}
                <strong>{displayUser?.full_name || displayUser?.email}</strong>
              </span>
              {displayUser?.patient_id && (
                <span className="text-xs bg-white/20 rounded px-2 py-0.5">
                  {displayUser.patient_id}
                </span>
              )}
              {!isPatientImpersonation && displayUser?.role && (
                <span className="text-xs bg-white/20 rounded px-2 py-0.5">
                  {displayUser.role}
                </span>
              )}
              {displayUser?.clinic_name && (
                <span className="text-xs">
                  @ {displayUser.clinic_name}
                </span>
              )}
            </div>
          </div>

          {/* Right: Time remaining and exit button */}
          <div className="flex items-center gap-3">
            {timeRemaining && (
              <div className="flex items-center gap-1 text-sm bg-white/20 rounded px-2 py-1">
                <Clock className="h-3 w-3" />
                <span>{timeRemaining}</span>
              </div>
            )}
            
            <Button
              onClick={handleStopImpersonation}
              disabled={stopping}
              size="sm"
              className={`font-semibold ${isPatientImpersonation 
                ? 'bg-white text-teal-600 hover:bg-gray-100' 
                : 'bg-white text-red-600 hover:bg-gray-100'}`}
              data-testid="stop-impersonation-btn"
            >
              <LogOut className="h-4 w-4 mr-1" />
              {stopping ? 'Exiting...' : (isPatientImpersonation ? 'Exit Patient View' : 'Exit Impersonation')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
